<template>
  <div class="plan-card" :class="{ 'plan-card__selected': selected }">
    <div v-if="savingPercentage" class="plan-card__badge">
      Most Popular • Save {{ savingPercentage }}
    </div>
    <div class="plan-card__content">
      <div>
        <div class="plan-card__title">
          {{ plan.nickname }}
        </div>
        <p class="plan-card__price-text">
          {{ billedAtText }}
        </p>
        <TrialBadge
          v-if="!billing.isTrialOver && trialByPlan15535Treatment()"
          class="plan-card__trial-badge"
          :days="trialLength"
        />
      </div>
      <div class="plan-card__price">
        <div :class="{ 'plan-card__price-new': plan.id === 'plan_annual' }">
          {{ getPrice(plan.price) }}
        </div>
        <div
          v-if="billing.showExternalPayment16218"
          class="plan-card__price-subtitle16218"
        >
          Paying by card
        </div>
        <div
          v-else-if="plan.id === 'plan_annual' && oldPrice"
          class="plan-card__price-old"
        >
          {{ getPrice(oldPrice) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PlanView, BillingView } from '@/models/billing'
import { PlanPrice } from '@/models/billing.plans'
import { computed } from 'vue'
import TrialBadge from './TrialBadge.vue'
import { trialByPlan15535Treatment } from '@/services/ab/15535_trial_by_plan'

const props = defineProps<{
  plan: PlanView
  billing: BillingView
  selected: boolean
}>()

const oldPrice = computed(() => {
  if (props.plan.id !== 'plan_annual') {
    return
  }

  return props.plan.discount
    ? props.plan.priceNoDiscount
    : props.billing.monthlyPlan.price
})

const trialLength = computed(() => {
  return props.billing.trialLength(
    props.plan.id === 'plan_annual' ? 'annual' : 'monthly',
  )
})

function getPrice(price: PlanPrice): string {
  if (props.plan.id === 'plan_annual' && props.billing.platform === 'android') {
    // On Android we can't show the annual plan's price per month due to
    // the Play Store rules.
    return price.withPeriod.perYear
  }

  return price.withPeriod.perMonth
}

const savingPercentage = computed(() => {
  if (props.plan.id !== 'plan_annual') {
    return null
  }
  if (props.plan.discount?.type === 'percentage') {
    return `${props.plan.discount.value}%`
  }
  return `${props.billing.switchToAnnualSavings.percent}%`
})

const billedAtText = computed(() => {
  if (
    props.plan.nickname === 'Monthly' ||
    props.billing.platform === 'android'
  ) {
    return `Billed ${props.plan.labels.frequency}`
  }
  return `Billed ${props.plan.labels.frequency} at ${props.plan.price.perPeriod}`
})
</script>

<style scoped lang="scss">
.plan-card {
  border-radius: 12px;
  border: 1px solid $c-black-13;
  padding: 24px 28px;
  position: relative;
  margin-inline: auto;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 552px;
  max-width: 100%;

  @media (max-width: $w-mobile-s) {
    width: 100%;
    padding: 24px 16px;
  }
  .night-mode & {
    border-color: $c-black-15;
  }

  &__badge {
    background: $c-blue-4;
    color: $c-white;
    padding: 7px 12px 5px;
    position: absolute;
    top: -14px;
    left: 28px;
    border-radius: 20px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;

    @media (max-width: $w-mobile-s) {
      left: 16px;
    }
  }

  &__title {
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
    letter-spacing: -0.4px;
    color: $c-black-15;

    @media (max-width: $w-mobile-s) {
      font-size: 18px;
    }
  }

  &__price-text {
    color: $c-black-6;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: -0.32px;
    margin-bottom: 8px;
    @media (max-width: $w-mobile-s) {
      font-size: 14px;
    }
  }

  &__content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  &__price-new {
    color: $c-red;
    text-align: right;
  }

  &__price-old,
  &__price-subtitle16218 {
    color: $c-black-6;
    text-decoration: line-through;
    font-size: 16px;
    text-align: right;
    font-weight: 400;
    line-height: 24px;

    @media (max-width: $w-mobile-s) {
      font-size: 14px;
    }
  }

  &__price-subtitle16218 {
    text-decoration: none;
  }

  &__price {
    font-size: 24px;
    line-height: 28px;
    font-weight: 500;
    letter-spacing: -0.48px;

    @media (max-width: $w-mobile-s) {
      font-size: 20px;
    }
  }

  &:not(&__selected) {
    @include hover {
      border-color: $c-black-6;
    }
  }

  &__trial-badge {
    color: $c-black-3;
    background-color: $c-bg;
  }

  &__selected {
    border-color: $c-blue-4;
    background: #3876f014;

    .plan-card__trial-badge {
      color: $c-blue-4;
      background-color: #3876f014;
    }
  }
}
.night-mode {
  .plan-card {
    border-color: $c-dark-5;
    background: rgba(56, 118, 240, 0.08);
    &__title {
      color: $c-white;
    }

    &__selected {
      border-color: $c-blue-4;
      background: rgba(56, 118, 240, 0.28);
    }
  }
}
</style>
